import {
  ElementRef,
  Directive,
  AfterViewInit,
  OnDestroy,
  Input,
  Renderer2,
  NgZone,
  Self,
  TemplateRef,
} from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Directive({
  selector: '[baseTruncate]',
  hostDirectives: [
    {
      directive: NgbTooltip,
      inputs: ['ngbTooltip', 'placement', 'triggers', 'container', 'disableTooltip'],
    },
  ],
})
export class BaseTextTruncateDirective implements AfterViewInit, OnDestroy {
  // Custom tooltip content managed by `baseTruncate`. Do not use `[ngbTooltip]` on the host; use `[customTooltip]` instead.
  @Input() customTooltip: string | TemplateRef<any> = '';
  @Input() hideTooltip = false;

  private resizeObserver?: ResizeObserver;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone,
    @Self() private tooltip: NgbTooltip
  ) {}

  ngAfterViewInit(): void {
    this.renderer.addClass(this.el, 'text-truncate');
    this.recalculate();
    this.ngZone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.recalculate());
        this.resizeObserver.observe(this.el);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.tooltip.close();
  }

  private onResize = () => this.recalculate();

  private get el(): HTMLElement {
    return this.elRef.nativeElement;
  }

  private recalculate(): void {
    requestAnimationFrame(() => {
      const el = this.el;
      const childTexts = Array.from(el.children)
        .map((c) => c.textContent?.trim())
        .filter((t) => t && t.length > 0);
      const isMultilineTooltip = childTexts.length > 1;

      const fullText = this.customTooltip
        ? this.customTooltip
        : isMultilineTooltip
        ? childTexts.join('\n')
        : (el.textContent || '').trim();

      const isOverflowing = el.scrollWidth > el.clientWidth;

      if (this.hideTooltip || !fullText || (!isOverflowing && !this.customTooltip)) {
        this.ngZone.run(() => {
          this.tooltip.disableTooltip = true;
          this.tooltip.close();
        });
        return;
      }

      this.ngZone.run(() => {
        this.tooltip.disableTooltip = false;
        this.tooltip.container = 'body';
        this.tooltip.placement = this.tooltip.placement || 'top';
        this.tooltip.triggers = this.tooltip.triggers || 'hover';
        if (isMultilineTooltip) {
          this.tooltip.tooltipClass = 'multiline-tooltip';
        }
        this.tooltip.ngbTooltip = fullText;
      });
    });
  }
}
